import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {Map, fromJS} from 'immutable';
import {Link} from 'react-router';
import {Banner, Anchor, Text} from '@albert-io/atomic';

import {upperFirst} from 'lib/stringUtils';
import {callAction} from 'client/framework';
import Wizard from 'client/generic/Wizard/Wizard.react';
import wizardStore from 'client/generic/Wizard/Wizard.store';
import LoadingIndicator from 'client/generic/LoadingIndicator.react';
import {getModelForResourceType} from 'resources/modelRegistry';

import createAssignmentActions from './CreateAssignment.actions';
import createAssignmentStore from './CreateAssignment.store';
import CreateAssignmentOptions from './CreateAssignmentOptions/CreateAssignmentOptions.react';
import CreateAssignmentRecipients from './CreateAssignmentRecipients/CreateAssignmentRecipients.react';
import CreateAssignmentConfirmation from './CreateAssignmentConfirmation/CreateAssignmentConfirmation.react';
import createAssignmentMetadataFromTopicMetadata from './utils/createAssignmentMetadataFromTopicMetadata';
import createAssignmentMetadataFromResourceMetadata from './utils/createAssignmentMetadataFromResourceMetadata';
import createAssignmentMetadataFromPracticeExam from './utils/createAssignmentMetadataFromPracticeExam';
import CreateAssignmentNoDataMsg from './shared/CreateAssignmentNoDataMsg.react';
import MarkdownRendererV2 from '../generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {CreateAssignmentContext} from './CreateAssignmentProvider';

import './create-assignment.scss';

export default class CreateAssignmentWrapper extends React.Component {
  static propTypes = {
    assignment: PropTypes.instanceOf(getModelForResourceType('assignment_v3')),
    assessment: PropTypes.instanceOf(getModelForResourceType('exam_v1')),
    folder: PropTypes.instanceOf(getModelForResourceType('template_v1')),
    licensedSubjects: ImmutablePropTypes.listOf(
      PropTypes.instanceOf(getModelForResourceType('subject_v2'))
    ),
    params: PropTypes.shape({
      assignmentId: PropTypes.string,
      examId: PropTypes.string,
      questionSetSlug: PropTypes.string,
      subjectSlug: PropTypes.string,
      folderId: PropTypes.string,
      topicSlug: PropTypes.string
    }),
    questionSets: ImmutablePropTypes.listOf(
      PropTypes.instanceOf(getModelForResourceType('question_set_v1'))
    ),
    folderQuestionSets: ImmutablePropTypes.listOf(
      PropTypes.instanceOf(getModelForResourceType('question_set_v1'))
    )
  };

  constructor() {
    super();

    this.state = {
      isReady: false
    };
  }

  componentDidMount() {
    this.setupAssignment();
  }

  componentDidUpdate(_, prevState) {
    if (!prevState.isReady) {
      this.setupAssignment();
    }
  }

  componentWillUnmount() {
    const {clearAssignmentProvider} = this.context;
    callAction(createAssignmentActions.RESET_STORE);
    clearAssignmentProvider();
  }

  /*
   We currently support URL params and context as methods for bootstrapping an assignment
   This function checks for either method, packages up the context data and launches this assignment creator

   It also handles assignment editing and handles the component loading state
  */
  setupAssignment = () => {
    const {
      questionSetList,
      questionDifficultyTotals,
      breadcrumbs,
      isLoading,
      hasError,
      assignmentName
    } = this.context;
    const creatorConfig = this.getConfigFromProps();
    // Only need to return since `isLoading` is already false in state from default value
    if (!creatorConfig && isLoading) {
      return;
    }
    /**
     *  If we don't have any params and the context isn't loading,
     *  the user likely refreshed the page while in the process of makaing an assignment
     *  or somehow paassed faulty url params
     */
    if (hasError || (!creatorConfig && !questionSetList && !isLoading)) {
      this.setState({
        hasError: true,
        isReady: true
      });
      return;
    }
    if (questionSetList) {
      // This translates the context data into the object that the createAssignment store expects
      const assignmentMetadata = Map({
        breadcrumbs,
        questionDifficultyTotals,
        questionSetList
      });
      callAction(createAssignmentActions.LAUNCH_ASSIGNMENT_CREATOR, {assignmentMetadata});
      if (assignmentName) {
        callAction(
          createAssignmentActions.SET_FIELD_STATE,
          Map({
            key: 'name',
            value: assignmentName
          })
        );
      }
    } else {
      callAction(createAssignmentActions.LAUNCH_ASSIGNMENT_CREATOR, creatorConfig);
    }

    /**
     * In the case of editing, we send all the student ID's we have on our model
     * to the store so the "Select Recipients" interface can populate correctly.
     */
    if (createAssignmentStore.getIsEditMode()) {
      callAction(
        createAssignmentActions.SET_MULTIPLE_RECIPIENTS,
        Map({
          addRecipients: true,
          autoAssignNewEnrollees: createAssignmentStore.isAutoAssignNewClassroomEnrollees(),
          classroomId: createAssignmentStore.getAssignmentClassroomId(),
          classroomName: createAssignmentStore.getAssignmentClassroomName(),
          students: createAssignmentStore.getStudents()
        })
      );
    }
    this.setState({isReady: true});
  };

  /**
   * @return {Object} config Instructions telling the ACF how to bootstrap itself
   */
  getConfigFromProps() {
    const {assignment, assessment, folder, folderQuestionSets, questionSets} = this.props;
    if (assignment) {
      return {
        assignment,
        assignmentMetadata: createAssignmentMetadataFromResourceMetadata(assignment),
        isEditMode: true
      };
    }
    if (assessment) {
      return {
        assignmentMetadata: createAssignmentMetadataFromPracticeExam(assessment)
      };
    }
    if (folder) {
      return {
        assignmentMetadata: createAssignmentMetadataFromResourceMetadata(folder, folderQuestionSets)
      };
    }
    if (questionSets) {
      return {
        assignmentMetadata: createAssignmentMetadataFromTopicMetadata(questionSets)
      };
    }
    return null;
  }

  renderReassignBanner() {
    if (!this.context) {
      return null;
    }
    const {resourceType} = this.context;
    const isReassign = resourceType === 'assignments_v3';
    const activeStep = wizardStore.getActiveStepNumber();

    if (!isReassign || activeStep > 1) {
      return null;
    }

    return (
      <Banner className=' u-mar-lr_auto u-mar-t_3 u-display_flex u-justify-content_center'>
        <Banner.Icon icon='info-circle' iconStyle='regular' className='u-align-self_center' />
        <Text>
          When reassigning an assignment, note that students will still have access to <br />
          the original assignment.{' '}
          <Anchor
            href='https://help.albert.io/en/articles/1884996-can-students-retake-an-assignment'
            target='_blank'
          >
            Learn more about re-assigning assignments.
          </Anchor>
        </Text>
      </Banner>
    );
  }

  getRecipientsWrapper() {
    return (
      <Wrapper heading='Choose Recipients' subheading='Step 1 of 3'>
        <CreateAssignmentRecipients licensedSubjects={this.props.licensedSubjects} />
      </Wrapper>
    );
  }

  getOptionsWrapper() {
    return (
      <Wrapper
        heading={`${upperFirst(createAssignmentStore.getAssignmentTypeLabel())} Options`}
        subheading='Step 2 of 3'
      >
        <CreateAssignmentOptions />
      </Wrapper>
    );
  }

  getConfirmationWrapper() {
    return <CreateAssignmentConfirmation />;
  }

  makeSteps() {
    return fromJS([
      {
        getContent: () => {
          return this.getRecipientsWrapper();
        },
        canAdvance: () => {
          return !createAssignmentStore.getSelectedRecipients().isEmpty();
        }
      },
      {
        getContent: () => {
          return this.getOptionsWrapper();
        },
        canAdvance: () => {
          return !createAssignmentStore.getSavedAssignmentPayload().isEmpty();
        }
      },
      {
        getContent: () => {
          return this.getConfirmationWrapper();
        },
        canRetreat: () => {
          return createAssignmentStore.getSavedAssignmentPayload().isEmpty();
        }
      }
    ]);
  }

  static contextType = CreateAssignmentContext;

  render() {
    if (this.state.hasError) {
      return (
        <div className='acf__wrapper'>
          <CreateAssignmentNoDataMsg />
        </div>
      );
    }
    if (!this.state.isReady) {
      return <LoadingIndicator />;
    }

    return (
      <>
        {this.renderReassignBanner()}
        <Wizard steps={this.makeSteps()} />
      </>
    );
  }
}

const Wrapper = (props) => {
  const generateBreadcrumbs = (breadcrumbs) => {
    return breadcrumbs.map((link, i) => {
      const isLinkValid = link.get('url') !== null;
      if (isLinkValid) {
        return (
          <Link className='acf__breadcrumb' key={i} to={link.get('url')}>
            <MarkdownRendererV2 text={link.get('name')} />
          </Link>
        );
      }
      return <span key={i}>{link.get('name')}</span>;
    });
  };

  const breadcrumbs = createAssignmentStore.getBreadcrumbs();
  const breadcrumbLabel = createAssignmentStore.getBreadcrumbLabel();
  const isEditMode = createAssignmentStore.getIsEditMode();
  const classrooms = isEditMode
    ? createAssignmentStore.getAssignmentModel().getClassrooms()
    : createAssignmentStore.getMyClassrooms();

  return (
    <div className='acf__wrapper'>
      <div className='acf__heading'>
        <h1>{props.heading}</h1>
        <h2>{props.subheading}</h2>
      </div>
      {!classrooms.isEmpty() && (
        <div className='acf__meta'>
          <b>{breadcrumbLabel}:</b>&nbsp;
          <span className='acf__breadcrumb-wrapper'>{generateBreadcrumbs(breadcrumbs)}</span>
          <div className='acf__question-counter'>
            <span className='acf__question-counter-item acf__question-counter--easy'>
              {createAssignmentStore.getTotalEasyQuestions()} easy
            </span>
            <span className='acf__question-counter-item acf__question-counter--moderate'>
              {createAssignmentStore.getTotalModerateQuestions()} moderate
            </span>
            <span className='acf__question-counter-item acf__question-counter--difficult'>
              {createAssignmentStore.getTotalDifficultQuestions()} difficult
            </span>
          </div>
        </div>
      )}
      {props.children}
    </div>
  );
};

Wrapper.propTypes = {
  heading: PropTypes.string.isRequired,
  subheading: PropTypes.string.isRequired,
  children: PropTypes.any
};
